import PropTypes from 'prop-types';
import { ChevronRight, ExternalLink } from 'lucide-react';
import { Text, Stack } from '@/components/ui/index.js';
import styles from './SettingsItem.module.css';

/**
 * SettingsItem - Individual settings row with icon, label, and action
 * Used for navigation items, links, and info displays
 */
export function SettingsItem({
  icon: Icon,
  label,
  value,
  onClick,
  href,
  external = false,
  showChevron = true,
  disabled = false,
  className = '',
  children,
  ...props
}) {
  const classNames = [
    styles.item,
    disabled && styles.disabled,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      <Stack direction="row" gap={3} align="center" className={styles.left}>
        {Icon && (
          <div className={styles.iconWrapper}>
            <Icon size={20} strokeWidth={2} />
          </div>
        )}
        <Stack direction="column" gap={0}>
          <Text size="base" color="primary" className={styles.label}>
            {label}
          </Text>
          {value && (
            <Text size="sm" color="muted" className={styles.value}>
              {value}
            </Text>
          )}
        </Stack>
      </Stack>
      <Stack direction="row" gap={2} align="center" className={styles.right}>
        {children}
        {external && <ExternalLink size={16} className={styles.chevron} />}
        {showChevron && !external && onClick && (
          <ChevronRight size={20} className={styles.chevron} />
        )}
      </Stack>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
        className={classNames}
        {...props}
      >
        {content}
      </a>
    );
  }

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={classNames}
        {...props}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={classNames} {...props}>
      {content}
    </div>
  );
}

SettingsItem.propTypes = {
  icon: PropTypes.elementType,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onClick: PropTypes.func,
  href: PropTypes.string,
  external: PropTypes.bool,
  showChevron: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};

export default SettingsItem;
